import { Injectable, inject } from "@angular/core";
import { WINDOW, LOCATION, Utilities } from "@tylertech/shared-module";
import { LoginView, WalkMeWindow } from "../interfaces";
import { SystemInfoService } from "./systemInfo.service";
import { StorageService } from "./storageService";
import { ToastService } from "./toast.service";
import { ShellService } from "@tylertech/incode-module";

@Injectable()
export class WalkMeService
{
    private readonly window = inject<WalkMeWindow>(WINDOW);
    private readonly location = inject<Location>(LOCATION);
    private readonly $shellService = inject(ShellService);
    private readonly $toastService = inject(ToastService);
    private readonly $systemInfoService = inject(SystemInfoService);
    private readonly $storageService = inject(StorageService);

    private initialized: boolean = false;
    protected scriptElement?: HTMLScriptElement;
    Enabled: boolean = false;

    initializeScript(product: string): void
    {
        if (this.initialized || this.$storageService.Get<boolean>("walkMeDisabled") || this.isLocal() || this.$shellService.isEmbedded())
            return;
        if (this.isTestEnvironment())
            product = product + "TEST";
        const src = this.WalkMeUrls[product];
        if (!src)
            return;
        this.scriptElement = document.createElement("script");
        this.scriptElement.type = "text/javascript";
        this.scriptElement.async = true;
        this.scriptElement.src = src;
        const s = document.getElementsByTagName("script")[0];
        s.parentNode.insertBefore(this.scriptElement, s);
        this.window._walkmeConfig = { smartLoad: true };
        this.initialized = true;
        this.Enabled = true;
    }

    setVariables(principle?: LoginView): void
    {
        if (!this.Enabled)
            return;
        const splitName = (principle?.Fullname ?? "").split(" ");
        const lastName = splitName[0]?.slice(0, -1) ?? "";
        const firstName = splitName[1] ?? "";
        const middleInitial = splitName[2] ?? "";

        this.$systemInfoService.getSystemInfo()
            .subscribe(r =>
            {
                this.window.tyler_variables = {
                    UserId: principle
                        ? `${principle.Tenant}.${principle.Fullname}.${r.Product}.${principle.UserId}`.replace(/\s/g, "")
                        : null,
                    Product: r.Product,
                    Email: principle ? principle.Email : null,
                    Name: principle ? principle.Fullname : null,
                    LastName: lastName,
                    FirstName: firstName,
                    MiddleInitial: middleInitial,
                    District: principle ? principle.Settings.Organization : null,
                    Persona: principle ? principle.UserPersona : null,
                    Version: r.SystemVersion,
                    IsInTheCloud: r.IsInTheCloud,
                    IsNewCloudUser: principle ? principle.Settings.IsNewCloudUser : false,
                    IsPositionChanging: principle ? principle.Settings.IsPositionChanging : false,
                    PositionChangeInfo: principle ? principle.Settings.PositionChangeInfo : null,
                    HireDate: principle ? principle.Settings.HireDate : null,
                    RehireDate: principle ? principle.Settings.RehireDate : null,
                    State: principle ? principle.Settings.State : null,
                };
            });
    }

    toggleWalkMe(): void
    {
        this.window.WalkMePlayerAPI.toggleMenu();
    }

    toggleWalkMePageDescriptions(): void
    {
        let flowId: number = 0;
        const currentUrl = this.location.href.toLowerCase();

        const flows = this.window.WalkMeAPI.getFlows().filter( x => this.pathMatch(currentUrl, x.Settings.CustomField1) && this.viewMatch(currentUrl, x.Settings.CustomField1));

        if (flows.length === 1)
            flowId = flows[0].Id;
        else
            flowId = flows.filter(x => this.elementMatch(x)).at(0)?.Id;
        if (!Utilities.IsNullOrUndefinedOrZero(flowId))
            this.window.WalkMeAPI.startFlowById(flowId);
        else
            this.$toastService.showToast("There are no field descriptions for this page", 3000, "primary");
    }

    private pathMatch(currentUrl: string, customField: string): boolean
    {
        if (!customField)
            return false;
        const path = customField.split(",").at(0);
        return currentUrl.indexOf(path.toLowerCase()) > -1;
    }

    private viewMatch(currentUrl: string, customField: string): boolean
    {
        if (!customField)
            return false;
        const view = customField.split(",").at(1);
        return !view || currentUrl.indexOf(view.toLowerCase()) > -1;
    }

    elementMatch(flow: any): boolean
    {
        const elements: string[] = flow.Settings.CustomField1.split(",").slice(2);
        if (elements.length === 0)
            return true;

        let match = true;
        Object.keys(elements).forEach(element =>
        {
            const xpath = `//*[text()='${elements[element]}']`;
            const matchingElement = document.evaluate(xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
            if (!matchingElement)
                match = false;
        });
        return match;
    }

    toggleWalkMeEnabled(): void
    {
        const disabled = this.$storageService.Get<boolean>("walkMeDisabled");
        this.$storageService.Set("walkMeDisabled", !disabled);
        // this.setupWalkMe();
    }

    /**
     * ! This code is a hack to work around walk me issues.
     *
     * WalkMe has a few cookies that do not have a bound size to them. Eventually the cookies start causing issues.
     * So if we get a large WalkMe cookie, just expire it.
     */
    forceExpireWalkMeCookies(): void
    {
        let d = location.hostname;
        if (d.indexOf('.') > -1)
            d = d.substring(d.indexOf('.'));

        for (const cookie of document.cookie.split(';').filter(c => c.length > 500 && c.trim().startsWith("wm-")))
        {
            document.cookie = cookie.trim().split('=')[0] + "=" +
                ";path=/" +
                ";domain=" + d +
                ";expires=Thu, 01 Jan 1970 00:00:01 GMT";
        }
    }

    //TODO: eventually find a better way to determine this
    private isTestEnvironment(): boolean
    {
        const hostName = this.location.hostname.toLowerCase();
        return hostName.indexOf("express-qa") > -1;
    }

    private isLocal(): boolean
    {
        const hostName = this.location.hostname.toLowerCase();
        return hostName.indexOf("localhost") > -1;
    }

    private readonly WalkMeUrls: Record<string, string> = {
        "Infinite VisionsTEST": "https://cdn.walkme.com/users/c4cdf287f44041949def89175951f522/test/walkme_c4cdf287f44041949def89175951f522_https.js",
        "Infinite Visions": "https://cdn.walkme.com/users/c4cdf287f44041949def89175951f522/walkme_c4cdf287f44041949def89175951f522_https.js",
        "Incode10TEST": "https://cdn.walkme.com/users/d9043310334341c786448b13d80d8d03/test/walkme_d9043310334341c786448b13d80d8d03_https.js",
        "Incode10": "https://cdn.walkme.com/users/d9043310334341c786448b13d80d8d03/walkme_d9043310334341c786448b13d80d8d03_https.js",
        "Incode9TEST": "https://cdn.walkme.com/users/840810cc17fa47e6a41a138da18353a1/test/walkme_840810cc17fa47e6a41a138da18353a1_https.js",
        "Incode9": "https://cdn.walkme.com/users/840810cc17fa47e6a41a138da18353a1/walkme_840810cc17fa47e6a41a138da18353a1_https.js"
    };
}
